import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ListRow from 'Douleutaras/components/Collapsible/ListRow';

import ServicePrice from './ServicePrice';
import ServiceInfoButton from './ServiceInfoButton';
import ServiceInfoModal, { ServiceInfoSection } from './ServiceInfoModal';
import { CatalogueService } from './types';

interface ServiceRowProps {
  service: CatalogueService;
  onServiceClick?(service: CatalogueService): void;
  /** Invoked when the row's ⓘ is clicked. The row's own booking action does not fire. */
  onInfoClick?(service: CatalogueService): void;
}

/**
 * A single service row, rendered through the reusable {@link ListRow}. `sub1` rows use the
 * strong (bold) emphasis; `sub2` rows use the regular emphasis. Any `sub2` children are
 * rendered as sibling rows.
 *
 * Clicking a row routes into the post-job (booking) flow — the same action as the
 * ProProfile CTA — via the `onServiceClick` callback threaded down from the catalogue. The
 * clicked row is handed back so the flow can pre-answer the form steps it stands for.
 *
 * A ⓘ sits inline after the label whenever the payload carries info copy for the row; it
 * opens the modal without triggering the row's own booking action.
 */
export const ServiceRow = ({ service, onServiceClick, onInfoClick }: ServiceRowProps) => {
  const { t } = useTranslation();
  const [infoOpen, setInfoOpen] = useState(false);
  const isSub1 = service.level === 'sub1';
  const children = service.children ?? [];

  const sections: Array<ServiceInfoSection> = [
    {
      heading: t('T.PROS-PROFILE.SERVICES.INFO-MODAL-INCLUDED', "What's included / excluded"),
      html: service.info?.includedExcluded ?? ''
    },
    {
      heading: t('T.PROS-PROFILE.SERVICES.INFO-MODAL-TERMS', 'General terms & conditions'),
      html: service.info?.termsAndConditions ?? ''
    }
  ].filter(section => Boolean(section.html));

  const hasInfo = sections.length > 0;

  const handleInfoClick = () => {
    setInfoOpen(true);

    if (onInfoClick) {
      onInfoClick(service);
    }
  };

  return (
    <>
      <ListRow
        label={service.label}
        emphasis={isSub1 ? 'strong' : 'regular'}
        labelAdornment={
          hasInfo && <ServiceInfoButton treatment="muted" onClick={handleInfoClick} />
        }
        trailing={service.price ? <ServicePrice price={service.price} /> : undefined}
        onClick={() => onServiceClick && onServiceClick(service)}
      />

      {hasInfo && (
        <ServiceInfoModal
          isOpen={infoOpen}
          title={service.label}
          sections={sections}
          priceNote
          onClose={() => setInfoOpen(false)}
        />
      )}

      {children.map(child => (
        <ServiceRow
          key={child.serviceId}
          service={child}
          onServiceClick={onServiceClick}
          onInfoClick={onInfoClick}
        />
      ))}
    </>
  );
};

export default ServiceRow;
